/*
 * Main product (PDP) controller — one instance per [data-main-product].
 *  - Swiper gallery (main carousel + thumbnails + prev/next)
 *  - Size/variant selection → price, availability, gallery media, ?variant URL
 *  - Quantity stepper
 *  - AJAX add to basket → dispatches `cart:change` (header bubble + cart drawer)
 *
 * Swiper is loaded globally (swiper-bundle, theme.liquid). Cart add mirrors
 * assets/product-card.js (fetch /cart/add.js → cart:change).
 */
(function () {
  // Shopper-facing wording from the theme's language file (snippets/js-strings.liquid).
  function str(key) {
    var s = window.notedStrings && window.notedStrings.product;
    return (s && s[key]) || '';
  }

  // Currency formatting must follow the customer's MARKET, not the browser
  // locale (Intl's default). A UK-locale browser on the EU store would otherwise
  // render euro prices as "€31.95" instead of "31,95 €". Map the market country
  // to a matching locale so Intl places the symbol / decimal per CLDR: euro is
  // symbol-after for most of the eurozone, symbol-before for NL and Ireland.
  function marketLocale(currency) {
    if (currency !== 'EUR') return undefined; // GBP etc — browser default is correct
    var country = (window.Shopify && Shopify.country) || 'GB';
    return { NL: 'nl-NL', IE: 'en-IE', MT: 'en-IE', CY: 'en-IE' }[country] || 'de-DE';
  }
  function money(cents) {
    var currency = window.Shopify && Shopify.currency ? Shopify.currency.active : 'GBP';
    try {
      return new Intl.NumberFormat(marketLocale(currency), {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (e) {
      return '£' + (cents / 100).toFixed(2);
    }
  }

  function initGallery(root) {
    var mainEl = root.querySelector('[data-gallery-main]');
    if (!mainEl || typeof Swiper === 'undefined') return null;

    var thumbsEl = root.querySelector('[data-gallery-thumbs]');
    var thumbsSwiper = null;
    if (thumbsEl) {
      thumbsSwiper = new Swiper(thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        freeMode: true,
        watchSlidesProgress: true
      });
    }

    // Mobile: one full-width 1:1 image per slide, swipe to the next, with a
    // full-width FILL progress bar (driven below). Desktop: one 560 image + thumbs.
    var fillEl = root.querySelector('[data-gallery-progress-fill]');
    function updateProgress(sw) {
      if (!fillEl || !sw || !sw.slides) return;
      var total = sw.slides.length || 1;
      var pct = ((sw.realIndex || 0) + 1) / total * 100;
      fillEl.style.width = pct + '%';
    }

    var mainSwiper = new Swiper(mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
      on: { init: updateProgress, slideChange: updateProgress }
    });

    var prev = root.querySelector('[data-gallery-prev]');
    var next = root.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', function () { mainSwiper.slidePrev(); });
    if (next) next.addEventListener('click', function () { mainSwiper.slideNext(); });

    return mainSwiper;
  }

  function init(root) {
    var variants = [];
    var jsonEl = root.querySelector('[data-variant-json]');
    if (jsonEl) {
      try { variants = JSON.parse(jsonEl.textContent); } catch (e) { variants = []; }
    }

    var mainSwiper = initGallery(root);

    function $all(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
    function setText(sel, text) { $all(sel).forEach(function (el) { el.textContent = text; }); }

    // Display + control elements exist in BOTH the in-page buy box and the sticky
    // bar, so collect all matches and keep them in lockstep.
    var comparePriceEls = $all('[data-compare-price]');
    var atcButtons = $all('[data-atc], [data-sticky-atc]');
    var idInput = root.querySelector('[data-variant-id-input]');
    var buttons = $all('[data-variant-button]');

    function slideToMedia(mediaId) {
      if (!mainSwiper || !mediaId) return;
      var slides = root.querySelectorAll('[data-gallery-main] .swiper-slide');
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
          mainSwiper.slideTo(i);
          return;
        }
      }
    }

    // Prefer the price string the server already rendered (country-correct via
    // snippets/money.liquid) so a switched variant matches the on-load format
    // exactly; fall back to the JS formatter for products with no size buttons.
    function priceStr(variant) {
      var btn = buttons.filter(function (x) { return x.getAttribute('data-variant-id') === String(variant.id); })[0];
      var f = btn && btn.getAttribute('data-variant-price-formatted');
      return f || money(variant.price);
    }

    function selectVariant(variant, mediaId) {
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      var priceFormatted = priceStr(variant);
      setText('[data-price]', priceFormatted);

      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      $all('[data-price]').forEach(function (el) { el.classList.toggle('text-coral-700', !!onSale); });
      comparePriceEls.forEach(function (el) {
        if (onSale) { el.textContent = money(variant.compare_at_price); el.hidden = false; }
        else { el.hidden = true; }
      });

      atcButtons.forEach(function (b) { b.disabled = !variant.available; });
      setText('[data-atc-label]', variant.available ? str('add_to_basket') : str('sold_out'));
      setText('[data-sticky-atc-label]', variant.available ? str('add') : str('sold_out'));
      setText('[data-atc-price]', variant.available ? priceFormatted : '');
      setText('[data-sticky-atc-price]', variant.available ? priceFormatted : '');

      // Sticky size pill — reflect the chosen size (read off a button carrying it).
      var srcBtn = root.querySelector('[data-size-label][data-variant-id="' + variant.id + '"]');
      if (srcBtn) setText('[data-sticky-size-label]', srcBtn.getAttribute('data-size-label'));

      // URL
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}

      slideToMedia(mediaId);
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('data-variant-available') === 'false') return;
        var id = parseInt(btn.getAttribute('data-variant-id'), 10);
        var variant = variants.filter(function (v) { return v.id === id; })[0];
        // Match by variant id (not node identity) so the in-page picker and the
        // sticky popover both highlight the chosen size.
        buttons.forEach(function (b) {
          var on = b.getAttribute('data-variant-id') === String(id);
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        selectVariant(variant, btn.getAttribute('data-variant-media-id'));
      });
    });

    // ── Native <select> fallback (non-size / multi-option products) ──
    var optionSelects = Array.prototype.slice.call(root.querySelectorAll('[data-variant-option-select]'));
    if (optionSelects.length) {
      optionSelects.forEach(function (sel) {
        sel.addEventListener('change', function () {
          var chosen = optionSelects.map(function (s) { return s.value; });
          var variant = variants.filter(function (v) {
            return v.options && v.options.length === chosen.length && v.options.every(function (opt, i) { return opt === chosen[i]; });
          })[0];
          if (variant) selectVariant(variant, variant.featured_media ? variant.featured_media.id : null);
        });
      });
    }

    // ── Quantity stepper ──
    var qty = root.querySelector('[data-qty-input]');
    var minus = root.querySelector('[data-qty-minus]');
    var plus = root.querySelector('[data-qty-plus]');
    function clampQty() {
      var n = parseInt(qty.value, 10);
      if (isNaN(n) || n < 1) n = 1;
      qty.value = n;
      return n;
    }
    if (qty && minus) minus.addEventListener('click', function () { qty.value = Math.max(1, clampQty() - 1); });
    if (qty && plus) plus.addEventListener('click', function () { qty.value = clampQty() + 1; });

    // ── AJAX add to basket (shared by the in-page form + the sticky bar) ──
    var form = root.querySelector('form[action$="/cart/add"], form[id^="product-form-"]');

    function addToCart() {
      if (!form) return;
      // Open the cart drawer immediately (spinner) so the add feels instant while
      // the request is in flight.
      document.dispatchEvent(new CustomEvent('cart:adding'));
      atcButtons.forEach(function (b) { b.disabled = true; });
      setText('[data-atc-label]', str('adding'));
      setText('[data-sticky-atc-label]', str('adding'));

      var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
      // Standard Shopify add-to-cart: serialize the whole product form so line item
      // properties, selling plans, and anything an app injects into the form
      // (e.g. Simple Bundles' properties[Selection N]) reach /cart/add.js. Building
      // a minimal {id, quantity} body drops them — the bundle add 422s. Do NOT set
      // Content-Type: the browser sets the multipart boundary for FormData. The
      // sticky ADD adds the same variant: the form's hidden variant input is kept
      // current by selectVariant, so reading the form covers both buttons.
      var formData = new FormData(form);
      if (qty) formData.set('quantity', clampQty());
      // Bundled section rendering: ask add.js to return the cart-drawer section in
      // the SAME response so the drawer renders in one round-trip (no extra cart.js
      // / update.js fetch). cart.js reads item_count from the rendered HTML.
      var drawer = document.querySelector('[data-cart-drawer]');
      if (drawer && drawer.dataset.sectionId) {
        formData.set('sections', drawer.dataset.sectionId);
        formData.set('sections_url', window.location.pathname);
      }
      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) throw res.data;
          document.dispatchEvent(new CustomEvent('cart:change', { detail: { sections: res.data.sections } }));
        })
        .catch(function (err) {
          console.error('[main-product] add to basket failed', err);
          document.dispatchEvent(new CustomEvent('cart:add-error', {
            detail: { message: (err && (err.description || err.message)) || '' }
          }));
        })
        .finally(function () {
          atcButtons.forEach(function (b) { b.disabled = false; });
          setText('[data-atc-label]', str('add_to_basket'));
          setText('[data-sticky-atc-label]', str('add'));
        });
    }

    if (form) {
      form.addEventListener('submit', function (e) { e.preventDefault(); addToCart(); });
      // Exposed for assets/bundle-ui.js: after its duplicate-selection warning is
      // confirmed it needs to finish the add WITHOUT re-firing the form's submit
      // event (which its own capture listener guards). See addConfirmed() there.
      window.notedProductAddToCart = addToCart;
    }
    // The sticky ADD submits the product form rather than calling addToCart()
    // directly, so the same guards as the main Add to Basket run first: on
    // bundles, assets/bundle-ui.js's capture submit listener shows its
    // "selections required" / "same product" modal and stops the add. The
    // form's own submit listener above then does the add when nothing objects.
    // (Not mainAtc.click(): an upsell app hooks that button's click and
    // swallows a scripted one.)
    var stickyAtcBtn = root.querySelector('[data-sticky-atc]');
    if (stickyAtcBtn) {
      stickyAtcBtn.addEventListener('click', function () {
        if (stickyAtcBtn.disabled) return;
        if (form && form.requestSubmit) form.requestSubmit();
        else addToCart();
      });
    }

    // ── Wishlist ──
    // Handled globally by assets/swym-wishlist.js (Swym v3 API bridge) — it
    // owns every `.swym-add-to-wishlist`, saves the form's selected variant on
    // the PDP, and keeps `.is-in-wishlist` truthful via Swym's event layer.

    // ── Sticky bar: reveal on scroll, size popover, jump nav ──
    var stickyBar = root.querySelector('[data-sticky-bar]');
    if (stickyBar) {
      // Reveal once the in-page buy box has scrolled above the viewport.
      //
      // Bar-from-load A/B test: CSS alone decides whether the bar is held open
      // on load (the Intelligems variant rule, or the theme's own when the
      // setting is "on"; see main-product.liquid). That rule also sets
      // --noted-sticky-on-load: 1 inside its phone-width media query, so reading
      // the computed value tells us what is on screen at the current width, and
      // aria-hidden follows it. Control never sets it: behaviour as before.
      var buyBox = root.querySelector('[data-buy-buttons]');
      var scrolledPast = false;
      var heldOpenByCss = function () {
        return getComputedStyle(stickyBar).getPropertyValue('--noted-sticky-on-load').trim() === '1';
      };
      var syncStickyAria = function () {
        var onScreen = scrolledPast || heldOpenByCss();
        stickyBar.setAttribute('aria-hidden', onScreen ? 'false' : 'true');
      };
      if (buyBox && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          var e = entries[0];
          scrolledPast = !e.isIntersecting && e.boundingClientRect.top < 0;
          stickyBar.classList.toggle('is-visible', scrolledPast);
          syncStickyAria();
        }, { threshold: 0 }).observe(buyBox);
      }
      syncStickyAria();
      // The test CSS can land after this runs (Intelligems injects it async):
      // the bar then slides in, so re-check when that transition ends, and on load.
      stickyBar.addEventListener('transitionend', function (e) { if (e.target === stickyBar) syncStickyAria(); });
      window.addEventListener('load', syncStickyAria);
      if (window.matchMedia) {
        var stickyMq = window.matchMedia('(max-width: 767px)');
        if (stickyMq.addEventListener) stickyMq.addEventListener('change', syncStickyAria);
        else if (stickyMq.addListener) stickyMq.addListener(syncStickyAria);
      }

      // Size popover — open/close + close on outside click or after a size is chosen.
      var sizeToggle = stickyBar.querySelector('[data-sticky-size-toggle]');
      var sizePanel = stickyBar.querySelector('[data-sticky-size-panel]');
      if (sizeToggle && sizePanel) {
        var closeSizes = function () {
          sizePanel.classList.remove('is-open');
          sizeToggle.classList.remove('is-open');
          sizeToggle.setAttribute('aria-expanded', 'false');
        };
        sizeToggle.addEventListener('click', function (e) {
          e.stopPropagation();
          var open = sizePanel.classList.toggle('is-open');
          sizeToggle.classList.toggle('is-open', open);
          sizeToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        document.addEventListener('click', function (e) {
          if (sizeToggle.contains(e.target) || sizePanel.contains(e.target)) return;
          closeSizes();
        });
        sizePanel.querySelectorAll('[data-sticky-size-option]').forEach(function (opt) {
          opt.addEventListener('click', closeSizes);
        });
      }

      // Jump nav — smooth scroll + geometry scrollspy. Targets resolve globally
      // (getElementById), so a tab reaches a block above OR a section added below.
      var tabs = Array.prototype.slice.call(stickyBar.querySelectorAll('[data-sticky-tab]'));
      var navTargets = tabs.map(function (t) {
        var id = t.getAttribute('data-sticky-target');
        var el = id ? document.getElementById(id) : null;
        return el ? { tab: t, id: id, el: el } : null;
      }).filter(Boolean);

      var setActiveTab = function (id) {
        navTargets.forEach(function (nt) {
          var on = nt.id === id;
          nt.tab.classList.toggle('is-active', on);
          if (on) nt.tab.setAttribute('aria-current', 'true');
          else nt.tab.removeAttribute('aria-current');
        });
      };

      // If a jump target is the anchor span sitting right before a collapsed
      // accordion (e.g. Description), open it so the shopper lands on the content.
      var openAccordionAt = function (anchorEl) {
        var d = anchorEl.nextElementSibling;
        if (d && d.matches && d.matches('details.main-product__accordion') && !d.open) {
          d.open = true;
        }
      };

      tabs.forEach(function (t) {
        t.addEventListener('click', function (e) {
          var id = t.getAttribute('data-sticky-target');
          var el = id ? document.getElementById(id) : null;
          if (!el) return; // graceful no-op when the anchor isn't on the page
          e.preventDefault();
          openAccordionAt(el); // open before scrolling so geometry is final
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveTab(id);
        });
      });

      if (navTargets.length) {
        var spyTicking = false;
        var updateSpy = function () {
          var line = 140; // ~header height + a little breathing room
          var bestId = null, bestTop = -Infinity, firstId = null, minTop = Infinity;
          navTargets.forEach(function (nt) {
            var top = nt.el.getBoundingClientRect().top;
            if (top <= line && top > bestTop) { bestTop = top; bestId = nt.id; }
            if (top < minTop) { minTop = top; firstId = nt.id; }
          });
          setActiveTab(bestId || firstId);
        };
        window.addEventListener('scroll', function () {
          if (spyTicking) return;
          spyTicking = true;
          window.requestAnimationFrame(function () { updateSpy(); spyTicking = false; });
        }, { passive: true });
        window.addEventListener('load', updateSpy);
        updateSpy();
      }
    }
  }

  // Scent-family badge override — show the family the shopper browsed in from
  // (stored on the collection page), not the server default. Use-once: read +
  // clear, then apply only if THIS product belongs to that family. So it lands on
  // the product clicked directly after a scent-family collection, then wipes.
  function applyScentFamily(root) {
    var raw = root.getAttribute('data-scent-families') || '';
    var fams = raw.split(';;').filter(Boolean).map(function (p) {
      var i = p.indexOf('~~');
      return { handle: p.slice(0, i), title: p.slice(i + 2) }; // "handle" holds the collection id
    });
    var stored = null;
    try {
      stored = localStorage.getItem('noted:lastScentFamily');
      localStorage.removeItem('noted:lastScentFamily');
    } catch (e) { return; }
    if (!stored || !fams.length) return;
    var match = fams.filter(function (f) { return f.handle === stored; })[0];
    if (!match) return;
    root.querySelectorAll('[data-scent-family-label]').forEach(function (el) {
      el.textContent = match.title;
    });
  }

  function boot() {
    document.querySelectorAll('[data-main-product]').forEach(function (root) {
      init(root);
      applyScentFamily(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Re-init in the theme editor when the section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-main-product]');
    if (root) init(root);
  });
})();
